import { array, literal, object, optional, type InferOutput } from 'valibot'
import Unexpected from '../../../../../Exception/Unexpected.class'
import isNotNil from '../../../../../ldsh/isNotNil'
import { Engine } from '../../../../engine/Engine.type'
import { EntityElevationSchema, type EntityElevation } from '../../../../entity/EntityElevation/EntityElevation.type'
import type { HasEntityFloorElevation } from '../../../../entity/EntityElevation/HasEntityFloorElevation.type'
import type { HasEntityWaterElevation } from '../../../../entity/EntityElevation/HasEntityWaterElevation.type'
import isEntityHasFloorElevation from '../../../../entity/EntityElevation/isEntityHasFloorElevation'
import isEntityHasWaterElevation from '../../../../entity/EntityElevation/isEntityHasWaterElevation'
import { EntityIdSchema } from '../../../../entity/EntityId.type'
import entityDisplayName from '../../../../entity/entityDisplayName'
import type { Entity } from '../../../../entity/index'
import findByIdOrThrow from '../../../../findByIdOrThrow'
import type { ActionLog } from '../../ActionLog.type'
import { ActionType } from '../ActionType'

const EntityElevationDataSchema = object({
  ent_id: EntityIdSchema,
  floor_e: optional(EntityElevationSchema),
  water_e: optional(EntityElevationSchema),
})
type EntityElevationData = InferOutput<typeof EntityElevationDataSchema>

export const SetEntityElevationActionSchema = object({
  type: literal(ActionType.Map.SetEntityElevation),
  data: array(EntityElevationDataSchema),
})

export type SetEntityElevationAction = InferOutput<typeof SetEntityElevationActionSchema>

export function createSetEntityElevationAction(
  data: Array<EntityElevationData>,
): SetEntityElevationAction {
  return {
    type: ActionType.Map.SetEntityElevation,
    data,
  }
}

export async function handleSetEntityElevationAction(
  engine: Engine,
  action: SetEntityElevationAction,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  actionLog: ActionLog
): Promise<void> {
  const { data } = action
  
  // findByIdOrThrow might be expensive, so we will keep them in a list
  const floorPairs = new Map<Entity & HasEntityFloorElevation, EntityElevation>
  const waterPairs = new Map<Entity & HasEntityWaterElevation, EntityElevation>

  // First, validate all entities exist and can have floor_e
  for (const update of data) {
    const { ent_id, floor_e, water_e } = update

    const ent = findByIdOrThrow(engine.state.ents, ent_id)
    if (isNotNil(floor_e)) {
      if (!isEntityHasFloorElevation(ent)) {
        throw new Unexpected(`Entity ${entityDisplayName(ent)} ${ent.id} does not support floor elevation changes`)
      }
      floorPairs.set(ent, floor_e)
    }
    if (isNotNil(water_e)) {
      if (!isEntityHasWaterElevation(ent)) {
        throw new Unexpected(`Entity ${entityDisplayName(ent)} ${ent.id} does not support water level changes`)
      }
      waterPairs.set(ent, water_e)
    }
  }

  // console.log('floorPairs', floorPairs)
  // console.log('waterPairs', waterPairs)

  // If we get here, all validations passed - apply the updates
  for (const [ent, floor_e] of floorPairs) {
    ent.floor_e = floor_e
  }
  for (const [ent, water_e] of waterPairs) {
    ent.water_e = water_e
  }
}
